import { Combobox, Item, ItemHeader, ItemTitle } from '@ibsys/kit';
import { IconButton, ListTruncationNotice, TruncatedText } from '@/shared/ui';
import { ExternalLinkIcon, XIcon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { KnowledgeSetListItem } from '@/entities/knowledge-set';
import { fetchKnowledgeSet, useUpdateKnowledgeSet } from '@/entities/knowledge-set';
import { knowledgeKeys } from '@/entities/knowledge';
import { useSearchQuery } from '@/entities/search';
import type { KnowledgeSetLink } from '@/entities/knowledge';
import { useComboboxServerSearch } from '@/shared/hooks';

/** Сколько сетов предлагать в подсказке: список набирается поиском по всему набору, а не листается */
const SET_SUGGESTIONS_LIMIT = 50;

type KnowledgeNodeSetsProps = {
  nodeId: number;
  knowledgeSets: KnowledgeSetLink[];
};

export const KnowledgeNodeSets: FC<KnowledgeNodeSetsProps> = ({ nodeId, knowledgeSets }) => {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: updateKnowledgeSet, isPending } = useUpdateKnowledgeSet();

  const { search: query, searchProps } = useComboboxServerSearch();

  // Варианты подбираются по фрагменту названия по всему набору сетов: сет за пределами
  // предложенной порции иначе к ноде не привязать. Поиск по смыслу (`search`) сюда не годится —
  // недописанное слово он не распознаёт, а выдача обновляется по мере набора
  const { data: searchData, isFetching } = useSearchQuery<KnowledgeSetListItem>({
    entityType: 'knowledge_set',
    filters: { isDeleted: false, name: query || undefined },
    limit: SET_SUGGESTIONS_LIMIT,
    offset: 0,
  });

  const linkedIds = useMemo(() => new Set(knowledgeSets.map((ks) => ks.id)), [knowledgeSets]);

  const comboboxOptions = useMemo(() => {
    const items = searchData?.items ?? [];
    return items.filter((item) => !linkedIds.has(item.id)).map((item) => ({ value: item.id, label: item.name }));
  }, [searchData, linkedIds]);

  const addToSet = useCallback(
    async (setId: number) => {
      const knowledgeSet = await fetchKnowledgeSet(setId);
      const newNodeIds = [...knowledgeSet.nodeIds, nodeId];
      await updateKnowledgeSet({
        id: setId,
        payload: { name: knowledgeSet.name, description: knowledgeSet.description, nodeIds: newNodeIds },
      });
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.details() });
    },
    [nodeId, updateKnowledgeSet, queryClient],
  );

  const removeFromSet = useCallback(
    async (setId: number) => {
      const knowledgeSet = await fetchKnowledgeSet(setId);
      const newNodeIds = knowledgeSet.nodeIds.filter((id) => id !== nodeId);
      await updateKnowledgeSet({
        id: setId,
        payload: { name: knowledgeSet.name, description: knowledgeSet.description, nodeIds: newNodeIds },
      });
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.details() });
    },
    [nodeId, updateKnowledgeSet, queryClient],
  );

  const handleAdd = useCallback(
    (value?: number | string) => {
      if (value == null) return;
      const setId = Number(value);
      const item = searchData?.items.find((i) => i.id === setId);
      if (!item) return;
      addToSet(setId);
    },
    [searchData, addToSet],
  );

  const handleNavigate = useCallback(
    (setId: number) => {
      navigate(`/knowledge-sets/${setId}`);
    },
    [navigate],
  );

  const hasKnowledgeSets = knowledgeSets.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-base font-semibold text-foreground">{t('knowledgeSets')}</h3>

      {hasKnowledgeSets && (
        <div className="flex flex-col gap-1">
          {knowledgeSets.map((ks) => (
            <Item key={ks.id} variant="outline" size="sm">
              <ItemHeader>
                <ItemTitle className="text-sm">
                  <TruncatedText>{ks.name}</TruncatedText>
                </ItemTitle>
                <div className="flex gap-1">
                  <IconButton
                    variant="ghost"
                    size="icon"
                    onClick={() => handleNavigate(ks.id)}
                    label={t('goToKnowledgeSet')}
                    icon={<ExternalLinkIcon className="size-3.5" />}
                  />
                  <IconButton
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFromSet(ks.id)}
                    disabled={isPending}
                    label={t('removeKnowledgeSet')}
                    icon={<XIcon className="size-3.5" />}
                  />
                </div>
              </ItemHeader>
            </Item>
          ))}
        </div>
      )}

      {!hasKnowledgeSets && <p className="text-sm text-muted-foreground">{t('noKnowledgeSets')}</p>}

      {/* Поле остаётся и при пустой выдаче: список наполняется поиском, и пустой он не
          означает, что привязывать нечего */}
      <div className="flex flex-col gap-1">
        <Combobox
          options={comboboxOptions}
          onChange={handleAdd}
          {...searchProps}
          isLoading={isFetching}
          disabled={isPending}
          placeholder={t('addKnowledgeSet')}
        />
        <ListTruncationNotice
          shown={searchData?.items.length ?? 0}
          total={searchData?.total}
          isSpaceReserved
          className="text-xs"
        />
      </div>
    </div>
  );
};
